import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import {
  Badge,
  Button,
  CheckCircleIcon,
  ClockIcon,
  PrinterIcon,
  Progress,
  RankSelect,
  RefreshIcon,
  VesselSelect,
} from '../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { generateCrewAiOverview, type CrewAiOverviewResponse } from '../../services/mutations';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import { CrewDataTableFrame, CrewDataTablePrompt, useCrewReportOpen } from './reportShared';
import type { OnboardStatusAiOverview, OnboardStatusResponse, OnboardStatusRow } from './types';

const displayDate = (value?: string | null) => (value ? formatDateGlobal(value) : '—');

const mapAiOverview = (result: CrewAiOverviewResponse): OnboardStatusAiOverview => ({
  id: result.id,
  generatedAt: result.generatedAt,
  model: result.model,
  readiness: result.overview.readiness,
  headline: result.overview.headline,
  summary: result.overview.summary,
  strengths: result.overview.strengths ?? [],
  risks: result.overview.risks ?? [],
  recommendations: result.overview.recommendations ?? [],
});

const rebuildSummary = (
  rows: OnboardStatusRow[],
  base: OnboardStatusResponse['summary'],
): OnboardStatusResponse['summary'] => {
  const withAi = rows.filter((r) => r.aiOverview);
  return {
    ...base,
    withAiOverview: withAi.length,
    ready: withAi.filter((r) => r.aiOverview?.readiness === 'READY').length,
    conditional: withAi.filter((r) => r.aiOverview?.readiness === 'CONDITIONAL').length,
    notReady: withAi.filter((r) => r.aiOverview?.readiness === 'NOT_READY').length,
  };
};

type BulkAiStatus = 'pending' | 'running' | 'done' | 'error' | 'cancelled';

type BulkAiItem = {
  crewId: number;
  name: string;
  crewRef?: string | null;
  rank: string | null;
  status: BulkAiStatus;
  progress: number;
  error?: string;
};

type BulkAiPhase = 'confirm' | 'running' | 'done';

const bulkStatusLabel = (status: BulkAiStatus) => {
  switch (status) {
    case 'pending':
      return 'Waiting';
    case 'running':
      return 'Refreshing…';
    case 'done':
      return 'Done';
    case 'error':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status;
  }
};

const readinessVariant = (readiness: OnboardStatusAiOverview['readiness']) => {
  switch (readiness) {
    case 'READY':
      return 'success' as const;
    case 'CONDITIONAL':
      return 'warning' as const;
    case 'NOT_READY':
      return 'danger' as const;
    default:
      return 'secondary' as const;
  }
};

const daysLabel = (days: number | null | undefined, overdueWord = 'overdue') => {
  if (days == null) return null;
  if (days < 0) return `${Math.abs(days)}d ${overdueWord}`;
  return `${days}d left`;
};

const formatWage = (row: OnboardStatusRow) => {
  if (row.wage == null) return '—';
  const formatted = row.wage.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return row.wageCurrency ? `${formatted} ${row.wageCurrency}` : formatted;
};

const Metric: React.FC<{ label: string; value: React.ReactNode; hint?: string | null; tone?: 'danger' | 'warning' | 'ok' }> = ({
  label,
  value,
  hint,
  tone,
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const toneClass =
    tone === 'danger'
      ? isDark
        ? 'text-red-300'
        : 'text-red-700'
      : tone === 'warning'
        ? isDark
          ? 'text-orange-300'
          : 'text-orange-700'
        : tone === 'ok'
          ? isDark
            ? 'text-emerald-300'
            : 'text-emerald-700'
          : colors.text;
  return (
    <div className="min-w-0">
      <p className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>{label}</p>
      <p className={cn('mt-0.5 text-sm font-semibold truncate', toneClass)}>{value}</p>
      {hint ? <p className={cn('text-[11px] truncate', colors.textMuted)}>{hint}</p> : null}
    </div>
  );
};

const AiColumn: React.FC<{
  title: string;
  items: string[];
  accent: 'strength' | 'risk' | 'rec';
  empty: string;
}> = ({ title, items, accent, empty }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const shell =
    accent === 'strength'
      ? isDark
        ? 'border-emerald-500/30 bg-emerald-950/25'
        : 'border-emerald-200 bg-emerald-50/80'
      : accent === 'risk'
        ? isDark
          ? 'border-red-500/30 bg-red-950/25'
          : 'border-red-200 bg-red-50/80'
        : isDark
          ? 'border-sky-500/30 bg-sky-950/25'
          : 'border-sky-200 bg-sky-50/80';
  const titleTone =
    accent === 'strength'
      ? isDark
        ? 'text-emerald-300'
        : 'text-emerald-800'
      : accent === 'risk'
        ? isDark
          ? 'text-red-300'
          : 'text-red-800'
        : isDark
          ? 'text-sky-300'
          : 'text-sky-800';
  const chip =
    accent === 'strength'
      ? isDark
        ? 'bg-emerald-900/50 text-emerald-100'
        : 'bg-white/80 text-emerald-900 border border-emerald-100'
      : accent === 'risk'
        ? isDark
          ? 'bg-red-900/50 text-red-100'
          : 'bg-white/80 text-red-900 border border-red-100'
        : isDark
          ? 'bg-sky-900/50 text-sky-100'
          : 'bg-white/80 text-sky-900 border border-sky-100';

  return (
    <div className={cn('rounded-xl border p-3 h-full min-h-[7.5rem]', shell)}>
      <p className={cn('text-[11px] font-semibold uppercase tracking-wider mb-2', titleTone)}>{title}</p>
      {items.length ? (
        <ul className="space-y-1.5">
          {items.map((item) => (
            <li key={item} className={cn('rounded-lg px-2.5 py-1.5 text-xs leading-snug', chip)}>
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className={cn('text-xs', colors.textMuted)}>{empty}</p>
      )}
    </div>
  );
};

const OnboardStatusCard: React.FC<{
  row: OnboardStatusRow;
  from: string;
  certWithinDays: number;
  refreshing: boolean;
  refreshDisabled: boolean;
  onRefreshAi: (crewId: number) => void;
}> = ({ row, from, certWithinDays, refreshing, refreshDisabled, onRefreshAi }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const ai = row.aiOverview;
  const certTone =
    row.certsExpired > 0 ? 'danger' : row.certsExpiring > 0 ? 'warning' : row.certsTotal > 0 ? 'ok' : undefined;
  const coeTone =
    row.daysToCoe == null ? undefined : row.daysToCoe < 0 ? 'danger' : row.daysToCoe <= 60 ? 'warning' : 'ok';
  const medicalTone =
    row.daysToMedical == null
      ? undefined
      : row.daysToMedical < 0
        ? 'danger'
        : row.daysToMedical <= 60
          ? 'warning'
          : 'ok';

  return (
    <article
      className={cn(
        'overflow-hidden rounded-2xl border shadow-sm',
        colors.border,
        colors.surface,
        row.notRecommended && (isDark ? 'ring-1 ring-red-500/40' : 'ring-1 ring-red-200'),
      )}
    >
      <div className={cn('border-b px-4 py-3.5 sm:px-5', colors.border)}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/crew/${row.crewId}`}
                state={{ from }}
                className={cn('text-base font-semibold hover:underline', colors.primaryFg)}
              >
                {row.name}
              </Link>
              {row.crewRef ? (
                <span
                  className={cn(
                    'inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                    colors.surfaceHover,
                    colors.textSecondary,
                  )}
                >
                  {row.crewRef}
                </span>
              ) : null}
              {row.notRecommended ? <Badge variant="danger">Not recommended</Badge> : null}
              {ai ? (
                <Badge variant={readinessVariant(ai.readiness)}>
                  {ai.readiness.replace(/_/g, ' ')}
                </Badge>
              ) : (
                <Badge variant="secondary">No AI overview</Badge>
              )}
            </div>
            <p className={cn('mt-1 text-sm', colors.textSecondary)}>
              {[row.rank, row.nationality, row.age != null ? `Age ${row.age}` : null]
                .filter(Boolean)
                .join(' · ') || '—'}
            </p>
            {row.contact ? <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{row.contact}</p> : null}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={ai ? 'outline' : 'accent'}
              size="sm"
              className="gap-1.5"
              disabled={refreshDisabled}
              onClick={() => onRefreshAi(row.crewId)}
              title={ai ? 'Re-run AI overview' : 'Generate AI overview'}
            >
              <RefreshIcon className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
              {refreshing ? 'Refreshing…' : ai ? 'Refresh AI' : 'Generate AI'}
            </Button>
            <Link
              to={`/crew/${row.crewId}`}
              state={{ from }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-medium',
                colors.border,
                colors.interactiveHover,
                colors.text,
              )}
            >
              Open file
            </Link>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Metric
            label="Vessel"
            value={row.vessel || '—'}
            hint={row.status === 'ONB' ? 'On board' : row.status}
          />
          <Metric
            label="Sign on"
            value={displayDate(row.signOnDate)}
            hint={row.monthsOnboard != null ? `${Math.round(row.monthsOnboard)} mo this tour` : null}
          />
          <Metric
            label="COE"
            value={displayDate(row.coeDate)}
            hint={daysLabel(row.daysToCoe)}
            tone={coeTone}
          />
          <Metric
            label="Cert health"
            value={`${row.certsExpired} exp · ${row.certsExpiring} ≤${certWithinDays}d`}
            hint={`${row.certsTotal} total`}
            tone={certTone}
          />
          <Metric
            label="Medical next due"
            value={displayDate(row.medicalNextDue)}
            hint={daysLabel(row.daysToMedical) ?? row.medicalKind}
            tone={medicalTone}
          />
          <Metric
            label="Wage"
            value={formatWage(row)}
            hint={
              row.appraisalAvg != null
                ? `Appraisal avg ${row.appraisalAvg.toFixed(1)}`
                : row.intentionSignOffDate
                  ? `Intention ${displayDate(row.intentionSignOffDate)}`
                  : row.availabilityStatus
            }
          />
        </div>

        {ai?.headline ? (
          <div className={cn('mt-4 rounded-xl border px-3 py-2.5', colors.border, colors.surfaceHover)}>
            <p className={cn('text-sm font-semibold', colors.text)}>{ai.headline}</p>
            {ai.summary ? (
              <p className={cn('mt-1 text-xs leading-relaxed', colors.textSecondary)}>{ai.summary}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="grid gap-3 p-4 sm:grid-cols-3 sm:px-5">
        <AiColumn
          title="Strengths"
          items={ai?.strengths ?? []}
          accent="strength"
          empty={ai ? 'None recorded' : refreshing ? 'Generating…' : 'Click Refresh AI to generate'}
        />
        <AiColumn
          title="Risks"
          items={ai?.risks ?? []}
          accent="risk"
          empty={ai ? 'None recorded' : refreshing ? 'Generating…' : 'Click Refresh AI to generate'}
        />
        <AiColumn
          title="Recommendations"
          items={ai?.recommendations ?? []}
          accent="rec"
          empty={ai ? 'None recorded' : refreshing ? 'Generating…' : 'Click Refresh AI to generate'}
        />
      </div>

      {ai ? (
        <div className={cn('border-t px-4 py-2 sm:px-5', colors.border)}>
          <p className={cn('text-[11px]', colors.textMuted)}>
            AI generated {displayDate(ai.generatedAt.slice(0, 10))} · {ai.model}
          </p>
        </div>
      ) : null}
    </article>
  );
};

type OnboardStatusFilters = {
  vesselId: string;
  appliedVesselId: string;
  rankId: string;
  query: string;
};

const OnBoardStatusPage: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const { listReturnPath } = useCrewReportOpen();
  const storageKey = getListStateStorageKey('/crew/onboard-status');
  const [persisted, setPersisted] = usePersistedListFilters<OnboardStatusFilters>(storageKey);
  const [vesselId, setVesselId] = useState(() => persisted?.vesselId ?? '');
  const [appliedVesselId, setAppliedVesselId] = useState(() => persisted?.appliedVesselId ?? '');
  const [rankId, setRankId] = useState(() => persisted?.rankId ?? '');
  const [query, setQuery] = useState(() => persisted?.query ?? '');
  const [refreshingCrewId, setRefreshingCrewId] = useState<number | null>(null);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPhase, setBulkPhase] = useState<BulkAiPhase>('confirm');
  const [bulkItems, setBulkItems] = useState<BulkAiItem[]>([]);
  const cancelBulkRef = useRef(false);

  useEffect(() => {
    setPersisted({ vesselId, appliedVesselId, rankId, query });
  }, [vesselId, appliedVesselId, rankId, query, setPersisted]);

  const listUrl = appliedVesselId
    ? `/crew/reports/onboard-status?vesselId=${encodeURIComponent(appliedVesselId)}`
    : null;
  const { data, error, isLoading, mutate } = useSWR<OnboardStatusResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const filteredRows = useMemo(() => {
    let rows = data?.rows ?? [];
    if (rankId) {
      const rid = Number(rankId);
      rows = rows.filter((row) => row.rankId === rid);
    }
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.name,
        row.crewRef,
        row.rank,
        row.nationality,
        row.aiOverview?.headline,
        row.aiOverview?.readiness,
        ...(row.aiOverview?.strengths ?? []),
        ...(row.aiOverview?.risks ?? []),
        ...(row.aiOverview?.recommendations ?? []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data?.rows, query, rankId]);

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    setAppliedVesselId(vesselId);
  }, [vesselId]);

  const applyAiToCache = useCallback(
    async (crewId: number, nextAi: OnboardStatusAiOverview) => {
      await mutate(
        (current) => {
          if (!current) return current;
          const rows = current.rows.map((row) =>
            row.crewId === crewId ? { ...row, aiOverview: nextAi } : row,
          );
          return {
            ...current,
            rows,
            summary: rebuildSummary(rows, current.summary),
          };
        },
        { revalidate: false },
      );
    },
    [mutate],
  );

  const bulkBusy = bulkPhase === 'running';
  const anyAiBusy = refreshingCrewId != null || bulkBusy;

  const handleRefreshAi = useCallback(
    async (crewId: number) => {
      if (anyAiBusy) return;
      setRefreshingCrewId(crewId);
      try {
        const result = await generateCrewAiOverview(crewId);
        await applyAiToCache(crewId, mapAiOverview(result));
        showSuccess('AI overview refreshed.');
      } catch (err: unknown) {
        const error = err as { response?: { data?: { message?: string | string[] } }; message?: string };
        const msg = error?.response?.data?.message;
        showError(
          Array.isArray(msg)
            ? msg.join(' ')
            : typeof msg === 'string'
              ? msg
              : error?.message || 'Could not refresh AI overview.',
        );
      } finally {
        setRefreshingCrewId(null);
      }
    },
    [anyAiBusy, applyAiToCache, showError, showSuccess],
  );

  const openBulkRefresh = useCallback(() => {
    const rows = data?.rows ?? [];
    if (!rows.length || anyAiBusy) return;
    cancelBulkRef.current = false;
    setBulkItems(
      rows.map((row) => ({
        crewId: row.crewId,
        name: row.name,
        crewRef: row.crewRef,
        rank: row.rank,
        status: 'pending' as const,
        progress: 0,
      })),
    );
    setBulkPhase('confirm');
    setBulkOpen(true);
  }, [anyAiBusy, data?.rows]);

  const closeBulkModal = useCallback(() => {
    if (bulkPhase === 'running') {
      cancelBulkRef.current = true;
      return;
    }
    setBulkOpen(false);
    setBulkItems([]);
    setBulkPhase('confirm');
  }, [bulkPhase]);

  const stopBulkRefresh = useCallback(() => {
    cancelBulkRef.current = true;
  }, []);

  const runBulkRefresh = useCallback(async () => {
    if (!bulkItems.length || bulkPhase === 'running') return;
    cancelBulkRef.current = false;
    setBulkPhase('running');

    let ok = 0;
    let failed = 0;
    const queue = [...bulkItems];

    for (let i = 0; i < queue.length; i++) {
      if (cancelBulkRef.current) {
        setBulkItems((prev) =>
          prev.map((item, idx) =>
            idx >= i && item.status === 'pending'
              ? { ...item, status: 'cancelled', progress: 0 }
              : item,
          ),
        );
        break;
      }

      const target = queue[i];
      setBulkItems((prev) =>
        prev.map((item) =>
          item.crewId === target.crewId
            ? { ...item, status: 'running', progress: 15, error: undefined }
            : item,
        ),
      );

      const pulse = window.setInterval(() => {
        setBulkItems((prev) =>
          prev.map((item) =>
            item.crewId === target.crewId && item.status === 'running'
              ? { ...item, progress: Math.min(90, item.progress + 8) }
              : item,
          ),
        );
      }, 400);

      try {
        const result = await generateCrewAiOverview(target.crewId);
        window.clearInterval(pulse);
        await applyAiToCache(target.crewId, mapAiOverview(result));
        ok += 1;
        setBulkItems((prev) =>
          prev.map((item) =>
            item.crewId === target.crewId
              ? { ...item, status: 'done', progress: 100, error: undefined }
              : item,
          ),
        );
      } catch (err: unknown) {
        window.clearInterval(pulse);
        failed += 1;
        const error = err as { response?: { data?: { message?: string | string[] } }; message?: string };
        const msg = error?.response?.data?.message;
        const message = Array.isArray(msg)
          ? msg.join(' ')
          : typeof msg === 'string'
            ? msg
            : error?.message || 'Failed';
        setBulkItems((prev) =>
          prev.map((item) =>
            item.crewId === target.crewId
              ? { ...item, status: 'error', progress: 100, error: message }
              : item,
          ),
        );
      }
    }

    setBulkPhase('done');
    if (cancelBulkRef.current) {
      showError('Bulk AI refresh stopped.');
    } else if (failed === 0) {
      showSuccess(`AI refreshed for ${ok} crew.`);
    } else {
      showError(`AI refresh finished: ${ok} ok, ${failed} failed.`);
    }
  }, [applyAiToCache, bulkItems, bulkPhase, showError, showSuccess]);

  const bulkCounts = useMemo(() => {
    const total = bulkItems.length;
    const done = bulkItems.filter((i) => i.status === 'done').length;
    const failed = bulkItems.filter((i) => i.status === 'error').length;
    const running = bulkItems.filter((i) => i.status === 'running').length;
    const cancelled = bulkItems.filter((i) => i.status === 'cancelled').length;
    const finished = done + failed + cancelled;
    const overall = total ? Math.round((finished / total) * 100) : 0;
    return { total, done, failed, running, cancelled, finished, overall };
  }, [bulkItems]);

  const summary = data?.summary;

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hideBack
        hint={{
          title: 'Onboard Status (AI)',
          body: 'Current onboard crew with dashboard glance fields and AI strengths, risks, and recommendations from each crew file overview.',
        }}
        onSearch={handleSearch}
        searchDisabled={!vesselId}
        loading={isLoading}
        onRefresh={appliedVesselId ? () => mutate() : undefined}
        refreshDisabled={anyAiBusy}
        context={
          data?.vessel
            ? `${data.vessel.vesselName} · ${summary?.crewCount ?? 0} onboard · ${summary?.withAiOverview ?? 0} with AI`
            : undefined
        }
        trailingActions={
          appliedVesselId ? (
            <>
              <Link
                to={(() => {
                  const qs = new URLSearchParams({ vesselId: appliedVesselId });
                  if (rankId) qs.set('rankId', rankId);
                  if (query.trim()) qs.set('q', query.trim());
                  return `/crew/onboard-status/print?${qs.toString()}`;
                })()}
                className={cn(
                  'inline-flex h-10 items-center gap-2 rounded-lg border px-4 text-sm font-medium transition-colors box-border',
                  colors.border,
                  colors.interactiveHover,
                  colors.text,
                  (!data?.rows.length || anyAiBusy) && 'pointer-events-none opacity-50',
                )}
                title="Print onboard status"
                aria-disabled={!data?.rows.length || anyAiBusy}
                onClick={(e) => {
                  if (!data?.rows.length || anyAiBusy) e.preventDefault();
                }}
              >
                <PrinterIcon className="h-4 w-4" />
                Print
              </Link>
              {(data?.rows.length ?? 0) > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={anyAiBusy}
                  onClick={openBulkRefresh}
                  title="Refresh AI for all onboard crew"
                >
                  <RefreshIcon className="h-4 w-4" />
                  Refresh all AI
                </Button>
              ) : null}
            </>
          ) : null
        }
      >
        <CrewReportFilterField label="Vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            placeholder="Select vessel…"
            includeIds={vesselId ? [vesselId] : undefined}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Rank" width="md">
          <RankSelect
            name="rankId"
            value={rankId}
            onChange={(e) => setRankId(String(e.target.value))}
            placeholder="All ranks"
            searchPlaceholder="Search ranks…"
            disabled={!appliedVesselId}
            includeIds={rankId ? [rankId] : undefined}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Search" width="lg">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Crew, AI text…"
            className={cn(
              'h-10 w-full rounded-lg border px-3 text-sm outline-none box-border',
              colors.border,
              colors.surface,
              colors.text,
            )}
            disabled={!appliedVesselId}
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      {appliedVesselId && summary ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {[
            { label: 'Onboard', value: summary.crewCount },
            { label: 'With AI', value: summary.withAiOverview },
            { label: 'Ready', value: summary.ready, tone: 'ok' as const },
            { label: 'Conditional', value: summary.conditional, tone: 'warning' as const },
            { label: 'Not ready', value: summary.notReady, tone: 'danger' as const },
            { label: 'Cert attention', value: summary.certAttention, tone: 'warning' as const },
          ].map((kpi) => (
            <div
              key={kpi.label}
              className={cn('rounded-xl border px-3 py-2.5', colors.border, colors.surface)}
            >
              <p className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                {kpi.label}
              </p>
              <p
                className={cn(
                  'mt-0.5 text-lg font-semibold tabular-nums',
                  kpi.tone === 'ok'
                    ? isDark
                      ? 'text-emerald-300'
                      : 'text-emerald-700'
                    : kpi.tone === 'warning'
                      ? isDark
                        ? 'text-orange-300'
                        : 'text-orange-700'
                      : kpi.tone === 'danger'
                        ? isDark
                          ? 'text-red-300'
                          : 'text-red-700'
                        : colors.text,
                )}
              >
                {kpi.value}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      <CrewDataTableFrame>
        {!appliedVesselId ? (
          <CrewDataTablePrompt>Select a vessel, then search.</CrewDataTablePrompt>
        ) : error ? (
          <CrewDataTablePrompt>
            <span className="text-red-600">Could not load onboard status.</span>
          </CrewDataTablePrompt>
        ) : isLoading && !data ? (
          <CrewDataTablePrompt>Loading onboard crew…</CrewDataTablePrompt>
        ) : filteredRows.length === 0 ? (
          <CrewDataTablePrompt>
            {query.trim() || rankId
              ? 'No crew match these filters.'
              : 'No onboard crew for this vessel.'}
          </CrewDataTablePrompt>
        ) : (
          <div className="space-y-4 overflow-y-auto dashboard-scrollbar pr-0.5 pb-2">
            {filteredRows.map((row) => (
              <OnboardStatusCard
                key={row.crewId}
                row={row}
                from={listReturnPath}
                certWithinDays={data?.certWithinDays ?? 90}
                refreshing={refreshingCrewId === row.crewId}
                refreshDisabled={anyAiBusy}
                onRefreshAi={(crewId) => void handleRefreshAi(crewId)}
              />
            ))}
          </div>
        )}
      </CrewDataTableFrame>

      <Modal
        isOpen={bulkOpen}
        onClose={closeBulkModal}
        size="2xl"
        closeOnEscape={bulkPhase !== 'running'}
      >
        <ModalHeader>
          <ModalTitle>
            {bulkPhase === 'confirm'
              ? 'Refresh AI for all onboard crew'
              : bulkPhase === 'running'
                ? 'Refreshing AI overviews…'
                : 'AI refresh complete'}
          </ModalTitle>
        </ModalHeader>
        <ModalContent className={colors.headerBg}>
          <div className="space-y-4">
            <p className={cn('text-sm', colors.textSecondary)}>
              {data?.vessel?.vesselName ? (
                <>
                  Vessel <span className={cn('font-medium', colors.text)}>{data.vessel.vesselName}</span>
                  {' · '}
                </>
              ) : null}
              {bulkCounts.total} crew will be processed one by one. Cards update as each finishes.
            </p>

            {bulkPhase !== 'confirm' ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className={colors.textMuted}>
                    Overall · {bulkCounts.finished}/{bulkCounts.total}
                    {bulkCounts.done ? ` · ${bulkCounts.done} ok` : ''}
                    {bulkCounts.failed ? ` · ${bulkCounts.failed} failed` : ''}
                    {bulkCounts.cancelled ? ` · ${bulkCounts.cancelled} cancelled` : ''}
                  </span>
                  <span className={cn('font-semibold tabular-nums', colors.text)}>{bulkCounts.overall}%</span>
                </div>
                <Progress
                  value={bulkCounts.overall}
                  size="default"
                  variant={
                    bulkCounts.failed > 0 && bulkPhase === 'done'
                      ? 'warning'
                      : bulkPhase === 'done'
                        ? 'success'
                        : 'default'
                  }
                />
              </div>
            ) : null}

            <div
              className={cn(
                'max-h-[min(28rem,55vh)] space-y-2 overflow-y-auto rounded-xl border p-2 dashboard-scrollbar',
                colors.border,
                colors.surface,
              )}
            >
              {bulkItems.map((item) => {
                const barVariant =
                  item.status === 'done'
                    ? 'success'
                    : item.status === 'error'
                      ? 'danger'
                      : item.status === 'cancelled'
                        ? 'warning'
                        : 'default';
                return (
                  <div
                    key={item.crewId}
                    className={cn(
                      'rounded-lg border px-3 py-2.5',
                      colors.border,
                      item.status === 'running' && (isDark ? 'bg-sky-950/30' : 'bg-sky-50/80'),
                    )}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className={cn('text-sm font-medium truncate', colors.text)}>
                          {item.name}
                          {item.crewRef ? (
                            <span className={cn('ml-2 text-xs font-normal', colors.textMuted)}>
                              {item.crewRef}
                            </span>
                          ) : null}
                        </p>
                        <p className={cn('text-xs', colors.textMuted)}>
                          {[item.rank, bulkStatusLabel(item.status)].filter(Boolean).join(' · ')}
                          {item.error ? ` — ${item.error}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {item.status === 'done' ? (
                          <CheckCircleIcon
                            className={cn('h-4 w-4', isDark ? 'text-emerald-300' : 'text-emerald-600')}
                          />
                        ) : item.status === 'running' ? (
                          <RefreshIcon className={cn('h-4 w-4 animate-spin', colors.primaryFg)} />
                        ) : item.status === 'pending' ? (
                          <ClockIcon className={cn('h-4 w-4', colors.textMuted)} />
                        ) : null}
                        <span className={cn('text-xs font-semibold tabular-nums', colors.textMuted)}>
                          {item.progress}%
                        </span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Progress value={item.progress} size="sm" variant={barVariant} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          {bulkPhase === 'confirm' ? (
            <>
              <Button type="button" variant="outline" onClick={closeBulkModal}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void runBulkRefresh()}>
                <RefreshIcon className="h-4 w-4" />
                Start refresh
              </Button>
            </>
          ) : bulkPhase === 'running' ? (
            <Button type="button" variant="outline" onClick={stopBulkRefresh}>
              Stop after current
            </Button>
          ) : (
            <Button type="button" onClick={closeBulkModal}>
              Close
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default OnBoardStatusPage;
